/**
 * VoiceToFormModal — registrazione vocale che compila i campi di un form.
 *
 * Flusso (iter 250):
 *   1. L'utente preme "Inizia a parlare" → MediaRecorder cattura audio
 *   2. Può mettere in pausa e riprendere (pause/resume) — i chunk si accumulano
 *   3. Premi "Fatto" → mergiamo tutti i chunks in un unico Blob → invio backend
 *   4. Backend trascrive con Whisper → estrae JSON → ritorna campi compilati
 *   5. Il modal chiama onApply(fields) → il form padre aggiorna i suoi state
 *
 * Bug fix rispetto al vecchio flusso Sopralluogo: i segmenti vocali venivano
 * inviati uno alla volta e l'estrazione lavorava su un singolo segmento alla
 * volta, perdendo contesto (es. "Bergamo" detta nel 1° segmento veniva
 * ignorata se l'estrazione partiva dal 2°). Qui INVECE concateniamo tutti
 * i segmenti in un unico audio → un unico transcript → una sola estrazione.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Microphone, Pause, Play, Check, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import CheckListPromemoria from "@/pages/sopralluogo/CheckListPromemoria";

const MAX_DURATION_SEC = 180; // 3 min max per evitare derive vocali infinite

export default function VoiceToFormModal({ open, onClose, onApply, existingFields = {}, allowOverwrite = false, mode = "create" }) {
  // states
  const [state, setState] = useState("idle"); // idle | recording | paused | processing
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState(null);
  // iter v28 r20 — toggle "Sovrascrivi campi già compilati".
  // Default OFF (= comportamento safe attuale: non sovrascrive).
  // Quando ON viene propagato al parent via onApply che riceve un flag.
  const [overwrite, setOverwrite] = useState(false);

  // refs (no re-render)
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Cleanup on close
      cleanup();
      setState("idle");
      setSeconds(0);
      setTranscript("");
      setError(null);
    }
  }, [open]);

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch { /* no-op */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    chunksRef.current = [];
  };

  const startRecording = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Picks best codec available
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""].find(
        (m) => !m || MediaRecorder.isTypeSupported(m),
      ) || "";
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onerror = (e) => {
        console.warn("MediaRecorder error", e);
        setError("Errore microfono · riprova");
        cleanup();
        setState("idle");
      };
      recorder.start(1000); // chunk ogni secondo per resilienza
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_DURATION_SEC) {
            stopAndExtract();
            return s + 1;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      console.warn("Mic denied:", e);
      setError("Permesso microfono negato dal browser. Vai in impostazioni e attivalo.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      setState("paused");
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      setState("recording");
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_DURATION_SEC) {
            stopAndExtract();
            return s + 1;
          }
          return s + 1;
        });
      }, 1000);
    }
  };

  const stopAndExtract = async () => {
    if (!mediaRecorderRef.current || state === "processing") return;
    setState("processing");
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    // Wait for last ondataavailable to fire
    await new Promise((resolve) => {
      const r = mediaRecorderRef.current;
      r.onstop = () => resolve();
      if (r.state !== "inactive") r.stop();
      else resolve();
    });
    // Stop mic stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    // Merge chunks
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    if (blob.size < 800) {
      setError("Vocale troppo breve · prova a riregistrare");
      setState("idle");
      return;
    }
    // Send to backend
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      // Step 1: transcribe
      const { data: tr } = await api.post("/voice/transcribe-only", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullTranscript = (tr.transcript || "").trim();
      if (!fullTranscript) {
        setError("Non ho sentito nessuna parola · prova a parlare più vicino al microfono");
        setState("idle");
        return;
      }
      setTranscript(fullTranscript);
      // Step 2: extract fields
      const { data: ex } = await api.post("/voice/property-fields", {
        transcript: fullTranscript,
        existing_fields: existingFields,
      });
      const fields = ex.fields || {};
      if (Object.keys(fields).length === 0) {
        toast.warning("Non sono riuscito a estrarre dati dalla tua descrizione. Aggiungi i campi manualmente.");
        onClose();
        return;
      }
      onApply(fields, fullTranscript, { overwrite });
      toast.success(`${Object.keys(fields).length} campi ${overwrite ? "aggiornati" : "compilati"} con la voce`);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore estrazione vocale";
      setError(typeof msg === "string" ? msg : "Errore estrazione vocale");
      setState("idle");
    }
  };

  const cancelAndReset = () => {
    cleanup();
    setState("idle");
    setSeconds(0);
    setError(null);
  };

  if (!open) return null;

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      onClick={() => state === "idle" && onClose()}
      data-testid="voice-to-form-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-[440px] p-5 sm:p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7C8B6E] mb-1 inline-flex items-center gap-1.5">
              <Microphone size={11} weight="fill" /> {mode === "edit" ? "Aggiungi a voce" : "Crea parlando"}
            </div>
            <h2 className="text-[18px] font-semibold text-[#1C1C1A] leading-tight">
              {mode === "edit" ? "Aggiungi o correggi a voce" : "Descrivi l'immobile con la voce"}
            </h2>
            <p className="text-[12.5px] text-[#6B6A65] mt-1 leading-snug">
              {mode === "edit"
                ? "Dimmi cosa aggiungere o correggere. Di default tengo i tuoi campi già compilati intatti."
                : "Tipologia, m², città, prezzo, caratteristiche. Pronto compila il form per te."}
            </p>
          </div>
          <button onClick={() => state === "idle" && onClose()} disabled={state !== "idle"} className="text-[#9A968B] hover:text-[#1C1C1A] p-1 disabled:opacity-30" aria-label="Chiudi" data-testid="voice-form-close">
            <X size={18} />
          </button>
        </div>

        {/* iter v28 r20 — Toggle "sovrascrivi" visibile solo se allowOverwrite
            (= esiste già almeno un campo compilato). Permette all'utente di
            MODIFICARE a voce campi già pieni invece di ignorarli silenziosamente. */}
        {allowOverwrite && (
          <label className="flex items-start gap-2.5 mb-3 p-2.5 bg-[#FAF9F5] border border-[#EAE7DF] rounded-lg cursor-pointer hover:border-[#7C8B6E]/50 transition-colors" data-testid="voice-overwrite-toggle">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              className="mt-0.5 accent-[#7C8B6E]"
              disabled={state !== "idle"}
            />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-snug">Sovrascrivi i campi già compilati</div>
              <div className="text-[11px] text-[#6B6A65] leading-snug mt-0.5">
                {overwrite ? "Se dirò un valore nuovo, sostituirà quello esistente." : "I campi già compilati restano intatti, aggiungo solo i mancanti."}
              </div>
            </div>
          </label>
        )}

        {/* iter v28 r467 — User M1392: «in annuncio quando do il vocale,
            metti la guida di cosa dire come in sopralluogo». Stesso componente
            CheckListPromemoria del sopralluogo, auto-spunta dal transcript
            man mano che parla. Visibile in idle/recording/paused/processing.
            In modalità "edit" (campi già compilati) la guida resta utile
            per spuntare ciò che potresti aggiungere. */}
        <CheckListPromemoria transcript={transcript} />

        <div className="bg-[#FAF9F5] border border-[#EAE7DF] rounded-xl p-5 mb-4 text-center">
          {state === "idle" && (
            <>
              <button
                onClick={startRecording}
                className="w-20 h-20 rounded-full bg-[#C24E4E] hover:bg-[#A33D3D] text-white inline-flex items-center justify-center transition-all shadow-md hover:shadow-lg"
                data-testid="voice-form-start"
                aria-label="Inizia registrazione"
              >
                <Microphone size={32} weight="fill" />
              </button>
              <div className="text-[12.5px] text-[#6B6A65] mt-3 leading-snug">
                Tocca per <strong>iniziare</strong>. Max 3 minuti.<br/>
                Puoi mettere in pausa e riprendere quando vuoi.
              </div>
            </>
          )}          {(state === "recording" || state === "paused") && (
            <>
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className={`w-2.5 h-2.5 rounded-full ${state === "recording" ? "bg-[#C24E4E] animate-pulse" : "bg-[#9A968B]"}`} />
                <span className="text-[28px] font-mono tabular-nums font-semibold text-[#1C1C1A]">
                  {formatTime(seconds)}
                </span>
              </div>
              <div className="text-[12px] text-[#6B6A65] mb-4">
                {state === "recording" ? "Sto registrando…" : "In pausa · premi play per riprendere"}
              </div>
              <div className="flex items-center justify-center gap-3">
                {state === "recording" ? (
                  <button onClick={pauseRecording} className="w-12 h-12 rounded-full bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] inline-flex items-center justify-center transition-colors" data-testid="voice-form-pause" aria-label="Pausa">
                    <Pause size={18} weight="fill" className="text-[#1C1C1A]" />
                  </button>
                ) : (
                  <button onClick={resumeRecording} className="w-12 h-12 rounded-full bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] inline-flex items-center justify-center transition-colors" data-testid="voice-form-resume" aria-label="Riprendi">
                    <Play size={18} weight="fill" className="text-[#7C8B6E]" />
                  </button>
                )}
                <button onClick={stopAndExtract} className="w-12 h-12 rounded-full bg-[#7C8B6E] hover:bg-[#6A7860] inline-flex items-center justify-center transition-colors" data-testid="voice-form-stop" aria-label="Fatto, estrai dati">
                  <Check size={20} weight="bold" className="text-white" />
                </button>
                <button onClick={cancelAndReset} className="w-12 h-12 rounded-full bg-white border border-[#E5E7E0] hover:border-[#C24E4E] hover:text-[#C24E4E] inline-flex items-center justify-center transition-colors" data-testid="voice-form-cancel" aria-label="Annulla">
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            </>
          )}

          {state === "processing" && (
            <>
              <div className="w-20 h-20 rounded-full bg-[#7C8B6E]/20 mx-auto mb-3 inline-flex items-center justify-center">
                <div className="w-10 h-10 border-3 border-[#7C8B6E] border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="text-[13px] font-semibold text-[#1C1C1A]">Pronto sta capendo cosa hai detto…</div>
              <div className="text-[11.5px] text-[#6B6A65] mt-1">Pochi secondi · sto trascrivendo ed estraendo i campi</div>
              {transcript && (
                <div className="mt-3 text-[11px] text-[#9A968B] italic leading-snug max-h-[80px] overflow-y-auto px-2">
                  "{transcript.slice(0, 200)}{transcript.length > 200 ? "…" : ""}"
                </div>
              )}
            </>
          )}
        </div>

        {error && (
          <div className="bg-[#FFF6F4] border border-[#F0D5CC] rounded-lg px-3 py-2 mb-3 text-[12px] text-[#C24E4E]" data-testid="voice-form-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
